import axios from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import * as XLSX from "xlsx";
import DatePicker from "../../common/DatePicker";
import ErrorHandler from "../../common/ErrorHandler";
import Filters from "../../common/Filters/Filters";
import SearchBoxWithCustomQuery from "../../common/SearchBoxWithCustomQuery";
import Table from "../../common/Table/Table";
import { indianCurrencyFormater } from "../../helpers/functions/currencyFormater";
import { formatDate, getTime } from "../../helpers/functions/dateFormatter";
import { errorToast } from "../../react-toastfiy/toast";
import auctionService from "../../services/auction/auction.service";
import NewAuctionDashboard from "./NewAuctionDashboard";
import { isMobile } from "../../helpers/functions/isMobile";
import jwtDecode from "jwt-decode";
import userService from "../../services/user.service";
import useRemainingHeight from "../../hooks/useRemainingHeight";

function CompletedAuctionOverview() {
  const SMART_FILTERS_ALLOWED_ROLES = [
    "Auction Super Admin",
    "Developer",
    "Super Admin",
    "Auction CEO",
    "Business Analyst",
  ];
  const currentUser = useMemo(() => {
    try {
      return jwtDecode(localStorage.getItem("refreshToken"));
    } catch (error) {
      return {};
    }
  }, []);
  const canUseSmartFilters = SMART_FILTERS_ALLOWED_ROLES.includes(
    currentUser?.role,
  );

  const history = useHistory();

  const tableRef = useRef(null);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [searchOrDate, setSearchOrDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isFetchData, setIsFetchData] = useState(true);
  const [newView, setNewView] = useState(false);

  const [smartFiltersOn, setSmartFiltersOn] = useState(false);
  const [stateHeadStates, setStateHeadStates] = useState([]);
  const [externalSelectedFilters, setExternalSelectedFilters] = useState(null);
  const { height, ref } = useRemainingHeight(20, [loadingData]);

  const filtersList = [
    { key: "state_name", name: "State", id: "state_name" },
    { key: "super_segment", name: "Segment", id: "super_segment" },
    { key: "brand_name", name: "Financier", id: "brand_name" },
    {
      key: "auction_end_date_for_filter",
      name: "End Date",
      id: "auction_end_date_for_filter",
      valueType: "date",
      limit: 2,
    },
  ];

  const fetchData = async () => {
    try {
      setLoadingData(true);
      setIsFetchData(false);
      const currentTime = getTime(new Date(), {
        includeSeconds: true,
      });
      const form_data = new FormData();
      form_data.append("auction_status", "completed");
      form_data.append("start_date", startDate + " 00:00:01");
      form_data.append("end_date", `${endDate} ${currentTime}`);

      const source = axios.CancelToken.source();
      const res = await auctionService.GetLiveAuction(form_data, source);

      if (res?.status === 200) {
        const newRes = res?.data.map((val) => ({
          ...val,
          auction_end_date_for_filter: val?.end_date?.split(" ")[0],
          state: val.state_name,
          segment: val.segment_name,
          auction_end_time: val?.end_date,
          participating_percentage:
            val?.subscribed_users_count > 0
              ? (
                  (val?.participating_bidders_count /
                    val?.subscribed_users_count) *
                  100
                ).toFixed(2)
              : 0,
        }));

        setLoadingData(false);
        setFilteredData(newRes);
        setAllData(newRes);
      } else {
        setLoadingData(false);
        setFilteredData([]);
        setAllData([]);
        errorToast(res?.msg);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingData(false);
    }
  };

  const handleRedirect = (key, value) => {
    let assetDashboardPath = `/dashboard/live_dashboard/completed/assets_dashboard`;
    if (
      ["Developer", "Super Admin", "Business Analyst"].includes(
        currentUser?.role,
      )
    ) {
      assetDashboardPath = `/live_auction_dashboard`;
    }
    if (key === "auction_id") {
      history.push({
        pathname: assetDashboardPath,
        search: `?auction_id=${value?.auction_id}`,
        state: {
          filteredData: value,
          flag: "auction_id",
          auction_status: "completed",
        },
      });
    } else if (key === "bidded_asset_count") {
      history.push({
        pathname: assetDashboardPath,
        search: `?bidded_asset_count=${value?.auction_id}`,
        state: {
          filteredData: value,
          flag: "bidded_asset_count",
          auction_status: "completed",
        },
      });
    } else if (key === "asset_count") {
      history.push({
        pathname: assetDashboardPath,
        search: `?asset_count=${value?.auction_id}`,
        state: {
          filteredData: value,
          flag: "asset_count",
          auction_status: "completed",
        },
      });
    } else if (key === "participating_bidders_count") {
      history.push({
        pathname: assetDashboardPath,
        search: `?participating_bidders_count=${value?.auction_id}`,
        state: {
          filteredData: value,
          flag: "participating_bidders_count",
          auction_status: "completed",
        },
      });
    } else if (key === "nonparticipating_bidders_count") {
      history.push({
        pathname: assetDashboardPath,
        search: `?nonparticipating_bidders_count=${value?.auction_id}`,
        state: {
          filteredData: value,
          flag: "nonparticipating_bidders_count",
          auction_status: "completed",
        },
      });
    }
  };

  const table_columns = [
    {
      title: "#",
      data_key: "",
      render: (row, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: "Auction Id",
      data_key: "auction_id",
      sortable: true,
      render: (row) => {
        return (
          <>
            <span
              style={{ cursor: "pointer" }}
              className=" text-nowrap link-primary text-decoration-none"
              onClick={() => handleRedirect("auction_id", row)}
            >
              {row?.auction_id}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "Auction End Time",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <span
              style={{ cursor: "pointer", fontWeight: "bold" }}
              className="text-nowrap"
            >
              {/* <Timer targetDateString={row?.end_date} /> */}
              {row?.end_date}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "State",
      data_key: "state_name",
      sortable: false,
    },
    {
      title: "Segment",
      data_key: "super_segment",
      sortable: false,
    },
    {
      title: "Financier",
      data_key: "brand_name",
      sortable: false,
    },
    {
      title: "No of Assets",
      data_key: "asset_count",
      sortable: false,
    },
    {
      title: "Assets Bidded",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            {row?.bidded_asset_count !== 0 ? (
              <span
                style={{ cursor: "pointer" }}
                className=" text-nowrap link-primary text-decoration-none"
                onClick={() => handleRedirect("bidded_asset_count", row)}
              >
                {row?.bidded_asset_count}
              </span>
            ) : (
              <span className=" text-nowrap  text-decoration-none">
                {row?.bidded_asset_count}
              </span>
            )}
            /
            <span
              style={{ cursor: "pointer" }}
              className=" text-nowrap link-primary text-decoration-none"
              onClick={() => handleRedirect("asset_count", row)}
            >
              {row?.asset_count}
            </span>
          </>
        );
      },
    },
    {
      title: "Avg. Selling Price",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            <span className=" text-nowrap  text-decoration-none">
              {indianCurrencyFormater(row?.average_selling_price)}
            </span>
          </>
        );
      },
    },
    {
      title: "Bidders Participated",
      data_key: "",
      sortable: false,
      render: (row) => {
        return (
          <>
            {row?.participating_bidders_count !== 0 ? (
              <span
                style={{ cursor: "pointer" }}
                className=" text-nowrap link-primary text-decoration-none"
                onClick={() =>
                  handleRedirect("participating_bidders_count", row)
                }
              >
                {row?.participating_bidders_count}
              </span>
            ) : (
              <span className=" text-nowrap  text-decoration-none">
                {row?.participating_bidders_count}
              </span>
            )}
            /
            <span
              style={{ cursor: "pointer" }}
              className=" text-nowrap link-primary text-decoration-none"
              onClick={() =>
                handleRedirect("nonparticipating_bidders_count", row)
              }
            >
              {row?.subscribed_users_count}
            </span>{" "}
          </>
        );
      },
    },
    {
      title: "Bids Per Asset",
      data_key: "bids_per_asset",
      sortable: true,
    },
    {
      title: "Total Bids",
      data_key: "total_bids_count",
      sortable: true,
    },
    {
      title: "Participation Percentage",
      data_key: "participating_percentage",
      sortable: true,
      render: (row) => {
        return (
          <>
            <span className=" text-nowrap  text-decoration-none">
              {row?.participating_percentage}%
            </span>
          </>
        );
      },
    },
    {
      title: "Approval Count",
      data_key: "approved_count",
      sortable: true,
    },
  ];

  const handleDownloadExcel = (e, value) => {
    e.preventDefault();

    if (Object.entries(value)?.length > 0) {
      const data = [];
      const columns = [
        "Sr. No.",
        "Auction Id",
        "Auction End Time",
        "State",
        "Segment",
        "Financier",
        "No of Assets",
        "Assets Bidded",
        "Avg. Selling Price",
        "Bidders Participated",
        "Bids Per Asset",
        "Total Bids",
        "Participation Percentage",
        "Approval Count",
      ];
      data.push(columns);

      let serialNumber = 1;
      let totalAssetsBidded = 0;
      let totalAssetsCount = 0;

      let stateWiseBidders = {};
      let stateWiseSubscribedUsers = {};
      let grandTotalApprovalCount = 0;

      let total_auctions = 0;
      let total_participating_percentage = 0;

      Object.entries(value).forEach(([row, item]) => {
        const biddedAssets = Number(item?.bidded_asset_count) || 0;
        const auctionEndTime = item?.auction_end_time;

        const assetCount = Number(item?.asset_count) || 0;
        const biddersParticipated =
          Number(item?.participating_bidders_count) || 0;
        const subscribedUsers = Number(item?.subscribed_users_count) || 0;

        totalAssetsBidded += biddedAssets;
        totalAssetsCount += assetCount;

        if (!stateWiseBidders[item?.state]) {
          stateWiseBidders[item?.state] = biddersParticipated;
        }

        if (!stateWiseSubscribedUsers[item?.state]) {
          stateWiseSubscribedUsers[item?.state] = subscribedUsers;
        }
        const approvalCount = !isNaN(Number(item?.approved_count))
          ? Number(item?.approved_count)
          : 0;
        grandTotalApprovalCount += approvalCount;

        if (Number(item?.participating_percentage) > 0) {
          total_auctions += 1;
          total_participating_percentage +=
            Number(item?.participating_percentage) || 0;
        }
        const rowData = [
          serialNumber++,
          item?.auction_id || "-",
          auctionEndTime,
          item?.state || "-",
          item?.segment || "-",
          item?.brand_name || "-",
          assetCount || "-",
          `${biddedAssets} / ${assetCount}` || "-",
          item?.average_selling_price || "-",
          `${biddersParticipated} / ${subscribedUsers}` || "-",
          item?.bids_per_asset || "-",
          item?.total_bids_count || "-",
          Number(item?.participating_percentage) || 0,
          approvalCount,
        ];
        data.push(rowData);
      });

      const totalBiddersParticipated = Object.values(stateWiseBidders).reduce(
        (acc, count) => acc + count,
        0,
      );

      const totalSubscribedUsers = Object.values(
        stateWiseSubscribedUsers,
      ).reduce((acc, count) => acc + count, 0);
      const avg_participating_percentage = (
        total_auctions > 0 ? total_participating_percentage / total_auctions : 0
      ).toFixed(2);

      const totalRow = [
        "Total",
        "-",
        "-",
        "-",
        "-",
        "-",
        totalAssetsCount,
        `${totalAssetsBidded} / ${totalAssetsCount}`,
        "-",
        `${totalBiddersParticipated} / ${totalSubscribedUsers}`,
        "-",
        "-",
        avg_participating_percentage,
        grandTotalApprovalCount,
      ];
      data.push(totalRow);

      const worksheet = XLSX.utils.aoa_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Auction overview");

      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const dataBlob = new Blob([excelBuffer], {
        type: "application/octet-stream",
      });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(dataBlob);
      link.download = "Completed Auction Overview.xlsx";
      link.click();
      URL.revokeObjectURL(link.href);
    }
  };

  useEffect(() => {
    const currentDate = new Date();

    // Get the year and month from the current date
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    // Calculate the first day of the month
    const firstDayOfMonth = formatDate(new Date(year, month, 1), "yyyy-mm-dd");
    // Calculate the last day of the next month and then subtract one day
    // const lastDayOfMonth = formatDate(
    //   new Date(year, month + 1, 0),
    //   "yyyy-mm-dd"
    // );

    const today = formatDate(new Date(), "yyyy-mm-dd");

    // const yesterday = new Date(currentDate);

    // yesterday.setDate(currentDate.getDate() - 1);

    // setPreviousDayDate(yesterday);
    setStartDate(() => firstDayOfMonth);
    setEndDate(() => today);
  }, []);
  useEffect(() => {
    if (isFetchData && startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!canUseSmartFilters || !currentUser?.phone) return;

    const form_data = new FormData();
    form_data.append("phone", currentUser.phone);

    userService.GetStatesWithStateHeads(form_data).then((res) => {
      if (res?.status === 200) {
        setStateHeadStates(res?.data || []);
      }
    });
  }, [canUseSmartFilters, currentUser]);

  useEffect(() => {
    if (smartFiltersOn) {
      if (stateHeadStates.length > 0) {
        setExternalSelectedFilters({
          state_name: Object.fromEntries(
            stateHeadStates.map((state) => [state, true]),
          ),
        });
      }
    } else {
      setExternalSelectedFilters((prev) => (prev ? { state_name: {} } : null));
    }
  }, [smartFiltersOn, stateHeadStates]);

  try {
    if (newView)
      return (
        <NewAuctionDashboard
          mode="completed"
          onBack={() => setNewView(false)}
        />
      );
    return (
      <>
        <div className=" mt-2">
          <div
            className="mt-4"
            // style={{ backgroundColor: "#e6f9ff" }}
          >
            <div className="d-flex justify-content-between flex-md-row flex-column flex-wrap gap-2 mx-2 my-2">
              <div className="h5 align-items-start">
                <span className="fw-bold">Completed Auction Overview</span>
                <div
                  style={{
                    fontSize: "0.85rem",
                    color: "#666",
                    fontWeight: "400",
                    marginTop: "4px",
                  }}
                >
                  Summary of all closed auctions with final bidding related
                  statistics and approval counts.
                </div>
              </div>

              <div className=" d-flex justify-content-end gap-2 align-items-end flex-md-row flex-column">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-success align-self-center"
                  onClick={() => setNewView(true)}
                >
                  ✨ Try the new view
                </button>
                {canUseSmartFilters && (
                  <div className="d-flex align-items-center gap-1">
                    <label
                      htmlFor="smartFiltersSwitch"
                      className="text-muted cursor-pointer"
                      style={{ fontSize: "0.8rem" }}
                    >
                      Smart Filters
                    </label>
                    <div className="form-check form-switch mt-2 ps-0">
                      <input
                        className="form-check-input m-0 cursor-pointer"
                        type="checkbox"
                        id="smartFiltersSwitch"
                        aria-label="Toggle smart filters"
                        checked={smartFiltersOn}
                        disabled={stateHeadStates.length === 0}
                        onChange={() => setSmartFiltersOn((prev) => !prev)}
                      />
                    </div>
                  </div>
                )}
                {searchOrDate === "date" ? (
                  <div className="d-flex justify-content-end align-items-end gap-1">
                    <div className="col-4 col-sm-5">
                      <DatePicker
                        date={startDate}
                        id="start_date"
                        name="start_date"
                        placeholder="Start Date"
                        labelStyle={{ fontSize: "12px" }}
                        inputStyle={{ fontSize: "12px" }}
                        setDate={(e) => {
                          setStartDate(() => e);
                        }}
                        label="Start Date"
                        isRequired={false}
                      />
                    </div>
                    <div className="col-4 col-sm-5">
                      <DatePicker
                        date={endDate}
                        id="end_date"
                        name="end_date"
                        labelStyle={{ fontSize: "12px" }}
                        inputStyle={{ fontSize: "12px" }}
                        placeholder="End Date"
                        setDate={(e) => {
                          setEndDate(() => e);
                        }}
                        label="End Date"
                        isRequired={false}
                        maxDate={formatDate(new Date(), "yyyy-mm-dd")}
                      />
                    </div>
                    <button
                      className=" btn btn-outline-secondary"
                      onClick={() => {
                        fetchData();
                      }}
                      style={{ fontSize: "12px" }}
                      disabled={loadingData}
                    >
                      Go
                    </button>
                  </div>
                ) : searchOrDate === "search" ? (
                  <SearchBoxWithCustomQuery
                    allData={allData}
                    setFilteredData={setFilteredData}
                    placeholder="Search..."
                    return_array={true}
                    keysToSearchOn={[
                      "auction_id",
                      "state_name",
                      "segment_name",
                      "brand_name",
                    ]}
                    className={` ${isMobile ? "col-12" : ""}`}
                    useSearchBoxButton={false}
                  />
                ) : null}
                <div className="d-flex gap-2">
                  <button
                    className=" btn bg-danger-subtle text-danger"
                    style={
                      searchOrDate === "search"
                        ? {
                            height: "40px",
                            width: "40px",
                            borderRadius: "40px",
                            border: "1px solid red",
                          }
                        : {
                            height: "40px",
                            width: "40px",
                            borderRadius: "40px",
                          }
                    }
                    onClick={(e) => {
                      setSearchOrDate("search");
                    }}
                  >
                    <i className="bi bi-search"></i>
                  </button>
                  <button
                    className=" btn bg-danger-subtle text-danger"
                    style={
                      searchOrDate === "date"
                        ? {
                            height: "40px",
                            width: "40px",
                            borderRadius: "40px",
                            border: "1px solid red",
                          }
                        : {
                            height: "40px",
                            width: "40px",
                            borderRadius: "40px",
                          }
                    }
                    onClick={(e) => {
                      setSearchOrDate("date");
                    }}
                  >
                    <i className="bi bi-calendar4"></i>
                  </button>
                  <Filters
                    data={allData}
                    setData={setFilteredData}
                    filteredData={filteredData}
                    setFilteredData={setFilteredData}
                    filtersList={filtersList}
                    localStorageKey="completed_auction_overview_filters"
                    externalSelectedFilters={externalSelectedFilters}
                  />
                  {filteredData && filteredData.length > 0 && (
                    <button
                      className=" btn bg-danger-subtle text-danger"
                      style={{
                        height: "40px",
                        width: "40px",
                        borderRadius: "40px",
                      }}
                      onClick={(e) => {
                        handleDownloadExcel(e, filteredData);
                      }}
                    >
                      <i className="bi bi-download"></i>
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div ref={ref}>
              <Table
                columns={table_columns.filter((column) => column)}
                data={filteredData}
                loading={loadingData}
                no_data_msg="No data"
                // td_text_position="left"
                td_class="text-capitalize"
                td_style={{ fontSize: "0.9rem" }}
                th_text_position="start"
                td_text_position="start"
                loading_cols={table_columns.filter((column) => column).length}
                loading_rows={5}
                flag="Completed_OverView"
                maxTableHeight={`${height}px`}
                enablePagination={true}
              />
            </div>
          </div>
        </div>
      </>
    );
  } catch (error) {
    console.log(error);
    return <ErrorHandler error={error} />;
  }
}

export default CompletedAuctionOverview;
